import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ArrowLeft, Calculator, DollarSign, TrendingUp, Building2,
  Percent, Info, ChevronRight, RefreshCw, Sparkles, Crown,
  ArrowLeftRight, Landmark, AlertCircle, CheckCircle
} from 'lucide-react';
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from 'axios';
import { toast } from 'sonner';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import CountrySelector from '@/components/CountrySelector';
import { formatIrr } from '@/utils/irr';

const flagEmoji = { CA: '🇨🇦', US: '🇺🇸' };

// Helper to format currency
const formatCurrency = (value, symbol = '$') => {
  if (value === null || value === undefined) return '-';
  return `${symbol}${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Helper to format percent
const formatPercent = (value) => {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(2)}%`;
};

export default function TaxStrategy() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState(user?.preferences?.default_country || 'US');
  const [taxRules, setTaxRules] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // After-tax analysis state
  const [afterTaxInputs, setAfterTaxInputs] = useState({
    purchase_price: 500000,
    property_type: 'residential',
    purchase_year: new Date().getFullYear(),
    holding_period_years: 10,
    year_1_noi: 45000,
    noi_growth_rate: 2.0,
    loan_amount: 400000,
    interest_rate: 6.5,
    loan_term_years: 30,
    exit_cap_rate: 6.0,
    selling_costs_percent: 6.0,
    marginal_tax_rate: null,
    capital_gains_rate: 0.15,
    state_tax_rate: 0.05,
    cost_segregation: false,
    exchange_deferral: false
  });
  const [afterTaxResults, setAfterTaxResults] = useState(null);
  
  // Cost segregation state
  const [costSegInputs, setCostSegInputs] = useState({
    purchase_price: 500000,
    property_type: 'residential',
    purchase_year: new Date().getFullYear(),
    marginal_tax_rate: null
  });
  const [costSegResults, setCostSegResults] = useState(null);
  
  // Exchange deferral state
  const [exchangeInputs, setExchangeInputs] = useState({
    original_purchase_price: 400000,
    original_purchase_year: new Date().getFullYear() - 6,
    improvements_made: 50000,
    accumulated_depreciation: 75000,
    sale_price: 650000,
    sale_year: new Date().getFullYear(),
    replacement_price: 800000,
    replacement_down_payment_percent: 25,
    capital_gains_rate: 0.15,
    state_tax_rate: 0.05
  });
  const [exchangeResults, setExchangeResults] = useState(null);

  // Check tier access
  const userTier = user?.tier || 'light';
  const hasAccess = userTier === 'pro' || userTier === 'developer';

  useEffect(() => {
    if (hasAccess) {
      fetchTaxRules();
    }
  }, [countryCode, hasAccess]);

  const fetchTaxRules = async () => {
    try {
      const response = await axios.get(`${API}/tax/rules/${countryCode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaxRules(response.data);
    } catch (error) {
      console.error('Failed to fetch tax rules:', error);
      toast.error('Failed to load tax rules');
    }
  };

  const runAfterTaxAnalysis = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/tax/after-tax-analysis`, {
        ...afterTaxInputs,
        country_code: countryCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAfterTaxResults(response.data);
      toast.success('Analysis complete');
    } catch (error) {
      console.error('Failed to run analysis:', error);
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const runCostSegAnalysis = async () => {
    if (countryCode !== 'US') {
      toast.error('Cost segregation is only available for US properties');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/tax/cost-segregation`, {
        ...costSegInputs,
        country_code: countryCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCostSegResults(response.data);
      toast.success('Cost segregation analysis complete');
    } catch (error) {
      console.error('Failed to run cost seg analysis:', error);
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const runExchangeAnalysis = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/tax/exchange-deferral`, {
        ...exchangeInputs,
        country_code: countryCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExchangeResults(response.data);
      toast.success('Exchange analysis complete');
    } catch (error) {
      console.error('Failed to run exchange analysis:', error);
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  // Upgrade prompt for light tier users
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Button 
            variant="ghost" 
            className="mb-4 text-slate-400 hover:text-white"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="bg-slate-800/50 rounded-2xl p-8 border border-amber-500/30 text-center">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Tax Strategy Module</h1>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">
              Unlock advanced tax planning tools including depreciation schedules, 
              cost segregation analysis, and 1031 exchange modeling.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-slate-700/50 rounded-lg p-4">
                <DollarSign className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                <div className="text-sm font-medium">Before vs After-Tax IRR</div>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <Building2 className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
                <div className="text-sm font-medium">Cost Segregation</div>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <ArrowLeftRight className="w-6 h-6 text-violet-500 mx-auto mb-2" />
                <div className="text-sm font-medium">1031 Exchange</div>
              </div>
            </div>
            <Button 
              className="bg-amber-500 hover:bg-amber-600 text-black"
              onClick={() => navigate('/dashboard')}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Upgrade to Pro
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-slate-400 hover:text-white"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Landmark className="w-6 h-6 text-amber-500" />
                Tax Strategy Module
              </h1>
              <p className="text-slate-400 text-sm">Optimize your tax strategy with advanced analysis tools</p>
            </div>
          </div>
          
          {/* Country Selector */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400">Country:</span>
            <Select value={countryCode} onValueChange={setCountryCode}>
              <SelectTrigger className="w-[180px] bg-slate-800 border-slate-700">
                <SelectValue>
                  <span className="flex items-center gap-2">
                    <span>{flagEmoji[countryCode]}</span>
                    {countryCode === 'US' ? 'United States' : 'Canada'}
                  </span>
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="US">
                  <span className="flex items-center gap-2">🇺🇸 United States</span>
                </SelectItem>
                <SelectItem value="CA">
                  <span className="flex items-center gap-2">🇨🇦 Canada</span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-800/50 border border-slate-700 p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-slate-700">
              Tax Rules Overview
            </TabsTrigger>
            <TabsTrigger value="after-tax" className="data-[state=active]:bg-slate-700">
              After-Tax Analysis
            </TabsTrigger>
            {countryCode === 'US' && (
              <TabsTrigger value="cost-seg" className="data-[state=active]:bg-slate-700">
                Cost Segregation
              </TabsTrigger>
            )}
            <TabsTrigger value="exchange" className="data-[state=active]:bg-slate-700">
              {countryCode === 'US' ? '1031 Exchange' : 'Section 44 Rollover'}
            </TabsTrigger>
          </TabsList>

          {/* Tax Rules Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {taxRules ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Depreciation */}
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-cyan-500" />
                    Depreciation Rules
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Method</span>
                      <span className="font-medium">{taxRules.depreciation.method.toUpperCase()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Residential Recovery</span>
                      <span className="font-medium">{taxRules.depreciation.residential_years} years</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Commercial Recovery</span>
                      <span className="font-medium">{taxRules.depreciation.commercial_years} years</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Land Allocation</span>
                      <span className="font-medium">{taxRules.depreciation.land_allocation_default}%</span>
                    </div>
                    {taxRules.depreciation.bonus_depreciation_available && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">Bonus Depreciation</span>
                        <span className="font-medium text-emerald-400">{(taxRules.depreciation.bonus_rate * 100).toFixed(0)}%</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-400">Cost Seg Eligible</span>
                      <span className={taxRules.depreciation.cost_segregation_eligible ? 'text-emerald-400' : 'text-slate-500'}>
                        {taxRules.depreciation.cost_segregation_eligible ? '✓ Yes' : '✗ No'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Capital Gains */}
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                    Capital Gains
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Inclusion Rate</span>
                      <span className="font-medium">{(taxRules.capital_gains.inclusion_rate * 100).toFixed(1)}%</span>
                    </div>
                    <div className="text-sm text-slate-400 mt-2 p-3 bg-slate-700/30 rounded-lg">
                      {taxRules.capital_gains.explanation}
                    </div>
                  </div>
                </div>

                {/* Exchange Deferral */}
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <ArrowLeftRight className="w-5 h-5 text-violet-500" />
                    {taxRules.exchange_deferral.name}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Available</span>
                      <span className={taxRules.exchange_deferral.available ? 'text-emerald-400' : 'text-red-400'}>
                        {taxRules.exchange_deferral.available ? '✓ Yes' : '✗ No'}
                      </span>
                    </div>
                    <div className="text-sm text-slate-400 mt-2 p-3 bg-slate-700/30 rounded-lg">
                      {taxRules.exchange_deferral.description}
                    </div>
                  </div>
                </div>

                {/* Passive Activity */}
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5 text-amber-500" />
                    Passive Activity Rules
                  </h3>
                  <div className="space-y-3">
                    {taxRules.passive_activity.loss_limit && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">Annual Loss Limit</span>
                        <span className="font-medium">{formatCurrency(taxRules.passive_activity.loss_limit)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-400">RE Pro Exception</span>
                      <span className={taxRules.passive_activity.real_estate_professional_exception ? 'text-emerald-400' : 'text-slate-500'}>
                        {taxRules.passive_activity.real_estate_professional_exception ? '✓ Available' : '✗ N/A'}
                      </span>
                    </div>
                    <div className="text-sm text-slate-400 mt-2 p-3 bg-slate-700/30 rounded-lg">
                      {taxRules.passive_activity.explanation}
                    </div>
                  </div>
                </div>

                {/* Default Rates */}
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 md:col-span-2">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Percent className="w-5 h-5 text-pink-500" />
                    Default Tax Rates
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-700/30 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-amber-400">
                        {(taxRules.default_rates.marginal_tax_rate * 100).toFixed(0)}%
                      </div>
                      <div className="text-sm text-slate-400">Marginal Rate</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-cyan-400">
                        {taxRules.default_rates.property_tax_range[0]}% - {taxRules.default_rates.property_tax_range[1]}%
                      </div>
                      <div className="text-sm text-slate-400">Property Tax Range</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">Loading tax rules...</div>
            )}
          </TabsContent>

          {/* After-Tax Analysis Tab */}
          <TabsContent value="after-tax" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input Form */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold mb-4">Property & Investment Details</h3>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <Label className="text-slate-400 text-sm">Purchase Price</Label>
                    <Input
                      type="number"
                      value={afterTaxInputs.purchase_price}
                      onChange={(e) => setAfterTaxInputs({...afterTaxInputs, purchase_price: Number(e.target.value)})}
                      className="bg-slate-700 border-slate-600"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Loan Amount</Label>
                    <Input
                      type="number"
                      value={afterTaxInputs.loan_amount}
                      onChange={(e) => setAfterTaxInputs({...afterTaxInputs, loan_amount: Number(e.target.value)})}
                      className="bg-slate-700 border-slate-600"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Year 1 NOI</Label>
                    <Input
                      type="number"
                      value={afterTaxInputs.year_1_noi}
                      onChange={(e) => setAfterTaxInputs({...afterTaxInputs, year_1_noi: Number(e.target.value)})}
                      className="bg-slate-700 border-slate-600"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Interest Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={afterTaxInputs.interest_rate}
                      onChange={(e) => setAfterTaxInputs({...afterTaxInputs, interest_rate: Number(e.target.value)})}
                      className="bg-slate-700 border-slate-600"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Holding Period (Years)</Label>
                    <Input
                      type="number"
                      value={afterTaxInputs.holding_period_years}
                      onChange={(e) => setAfterTaxInputs({...afterTaxInputs, holding_period_years: Number(e.target.value)})}
                      className="bg-slate-700 border-slate-600"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Exit Cap Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={afterTaxInputs.exit_cap_rate}
                      onChange={(e) => setAfterTaxInputs({...afterTaxInputs, exit_cap_rate: Number(e.target.value)})}
                      className="bg-slate-700 border-slate-600"
                    />
                  </div>
                </div>

                <div className="border-t border-slate-700 pt-4 mb-4">
                  <h4 className="font-medium mb-3">Tax Options</h4>
                  <div className="space-y-3">
                    {countryCode === 'US' && (
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm">Cost Segregation</span>
                          <p className="text-xs text-slate-400">Accelerate depreciation in Year 1</p>
                        </div>
                        <Switch
                          checked={afterTaxInputs.cost_segregation}
                          onCheckedChange={(checked) => setAfterTaxInputs({...afterTaxInputs, cost_segregation: checked})}
                        />
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm">{countryCode === 'US' ? '1031 Exchange at Exit' : 'Section 44 Rollover'}</span>
                        <p className="text-xs text-slate-400">Defer taxes on sale</p>
                      </div>
                      <Switch
                        checked={afterTaxInputs.exchange_deferral}
                        onCheckedChange={(checked) => setAfterTaxInputs({...afterTaxInputs, exchange_deferral: checked})}
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                  onClick={runAfterTaxAnalysis}
                  disabled={loading}
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Calculator className="w-4 h-4 mr-2" />
                  )}
                  Calculate After-Tax Returns
                </Button>
              </div>

              {/* Results */}
              <div className="space-y-4">
                {afterTaxResults ? (
                  <>
                    {/* IRR Comparison */}
                    <div className="bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-xl p-6 border border-emerald-500/30">
                      <h3 className="text-lg font-semibold mb-4">Return Comparison</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-white">
                            {formatIrr(afterTaxResults.return_comparison.before_tax_irr)}
                          </div>
                          <div className="text-sm text-slate-400">Before-Tax IRR</div>
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-emerald-400">
                            {formatIrr(afterTaxResults.return_comparison.after_tax_irr)}
                          </div>
                          <div className="text-sm text-slate-400">After-Tax IRR</div>
                        </div>
                      </div>
                      <div className="mt-4 text-center p-3 bg-slate-800/50 rounded-lg">
                        <p className="text-sm">{afterTaxResults.insight.headline}</p>
                      </div>
                    </div>

                    {/* Tax Benefits */}
                    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                      <h3 className="text-lg font-semibold mb-4">Tax Benefits</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Total Depreciation</span>
                          <span className="font-medium">{formatCurrency(afterTaxResults.tax_benefits.total_depreciation)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Total Tax Savings</span>
                          <span className="font-medium text-emerald-400">{formatCurrency(afterTaxResults.tax_benefits.total_depreciation_tax_savings)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Avg Annual Savings</span>
                          <span className="font-medium">{formatCurrency(afterTaxResults.tax_benefits.average_annual_tax_savings)}/yr</span>
                        </div>
                      </div>
                    </div>

                    {/* Exit Analysis */}
                    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                      <h3 className="text-lg font-semibold mb-4">Exit Analysis</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Exit Value</span>
                          <span className="font-medium">{formatCurrency(afterTaxResults.exit_analysis.projected_exit_value)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Total Gain</span>
                          <span className="font-medium">{formatCurrency(afterTaxResults.exit_analysis.total_gain)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Exit Taxes</span>
                          <span className="font-medium text-red-400">{formatCurrency(afterTaxResults.exit_analysis.exit_taxes)}</span>
                        </div>
                        {afterTaxResults.exit_analysis.exchange_tax_savings && (
                          <div className="flex justify-between bg-emerald-500/10 -mx-2 px-2 py-1 rounded">
                            <span className="text-emerald-400">Exchange Tax Savings</span>
                            <span className="font-medium text-emerald-400">{formatCurrency(afterTaxResults.exit_analysis.exchange_tax_savings)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Equity Multiple */}
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400">Equity Multiple (After-Tax)</span>
                        <span className="text-2xl font-bold text-cyan-400">
                          {afterTaxResults.return_comparison.after_tax_equity_multiple.toFixed(2)}x
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center text-slate-400">
                    <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Enter property details and click Calculate to see after-tax returns</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Cost Segregation Tab (US Only) */}
          {countryCode === 'US' && (
            <TabsContent value="cost-seg" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Input Form */}
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold mb-4">Cost Segregation Analysis</h3>
                  <p className="text-sm text-slate-400 mb-4">
                    A cost segregation study can accelerate depreciation by reclassifying building components 
                    into shorter recovery periods (5, 7, and 15 years).
                  </p>
                  
                  <div className="space-y-4 mb-6">
                    <div>
                      <Label className="text-slate-400 text-sm">Purchase Price</Label>
                      <Input
                        type="number"
                        value={costSegInputs.purchase_price}
                        onChange={(e) => setCostSegInputs({...costSegInputs, purchase_price: Number(e.target.value)})}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm">Property Type</Label>
                      <Select 
                        value={costSegInputs.property_type} 
                        onValueChange={(v) => setCostSegInputs({...costSegInputs, property_type: v})}
                      >
                        <SelectTrigger className="bg-slate-700 border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="residential">Residential</SelectItem>
                          <SelectItem value="commercial">Commercial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm">Marginal Tax Rate (optional)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Leave blank for default 32%"
                        value={costSegInputs.marginal_tax_rate || ''}
                        onChange={(e) => setCostSegInputs({...costSegInputs, marginal_tax_rate: e.target.value ? Number(e.target.value) : null})}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                  </div>

                  <Button 
                    className="w-full bg-cyan-500 hover:bg-cyan-600 text-black"
                    onClick={runCostSegAnalysis}
                    disabled={loading}
                  >
                    {loading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Building2 className="w-4 h-4 mr-2" />
                    )}
                    Analyze Cost Segregation
                  </Button>
                </div>

                {/* Results */}
                <div className="space-y-4">
                  {costSegResults?.eligible ? (
                    <>
                      {/* Component Allocation */}
                      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                        <h3 className="text-lg font-semibold mb-4">Component Allocation</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-slate-400">5-Year Property</span>
                            <span className="font-medium text-emerald-400">{formatCurrency(costSegResults.component_allocation['5_year_property'])}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">15-Year Property</span>
                            <span className="font-medium text-cyan-400">{formatCurrency(costSegResults.component_allocation['15_year_property'])}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Building (27.5/39-Year)</span>
                            <span className="font-medium">{formatCurrency(costSegResults.component_allocation.building_27_5_year)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Year 1 Comparison */}
                      <div className="bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-xl p-6 border border-emerald-500/30">
                        <h3 className="text-lg font-semibold mb-4">Year 1 Tax Impact</h3>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="text-center">
                            <div className="text-xl font-bold text-slate-300">
                              {formatCurrency(costSegResults.comparison.year_1.standard_depreciation)}
                            </div>
                            <div className="text-xs text-slate-400">Standard Depreciation</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-emerald-400">
                              {formatCurrency(costSegResults.comparison.year_1.with_cost_seg)}
                            </div>
                            <div className="text-xs text-slate-400">With Cost Seg</div>
                          </div>
                        </div>
                        <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                          <div className="text-2xl font-bold text-amber-400">
                            {formatCurrency(costSegResults.comparison.year_1.tax_savings)}
                          </div>
                          <div className="text-sm text-slate-400">Year 1 Tax Savings</div>
                        </div>
                      </div>

                      {/* ROI Analysis */}
                      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                        <h3 className="text-lg font-semibold mb-4">ROI Analysis</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Estimated Study Cost</span>
                            <span className="font-medium">{formatCurrency(costSegResults.roi_analysis.estimated_study_cost)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Payback Period</span>
                            <span className="font-medium text-emerald-400">{costSegResults.roi_analysis.study_payback_months} months</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">10-Year PV Savings</span>
                            <span className="font-medium">{formatCurrency(costSegResults.roi_analysis['10_year_present_value_savings'])}</span>
                          </div>
                          <div className="flex justify-between border-t border-slate-600 pt-2">
                            <span className="text-slate-400">Net Benefit</span>
                            <span className="font-bold text-emerald-400">{formatCurrency(costSegResults.roi_analysis.net_benefit)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">ROI</span>
                            <span className="font-bold text-amber-400">{costSegResults.roi_analysis.roi_percent}%</span>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : costSegResults?.eligible === false ? (
                    <div className="bg-amber-500/10 rounded-xl p-6 border border-amber-500/30">
                      <AlertCircle className="w-8 h-8 text-amber-500 mb-3" />
                      <h3 className="font-semibold mb-2">{costSegResults.message}</h3>
                      {costSegResults.alternative && (
                        <p className="text-sm text-slate-400">{costSegResults.alternative}</p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center text-slate-400">
                      <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Enter property details and click Analyze to see cost segregation benefits</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          )}

          {/* Exchange Deferral Tab */}
          <TabsContent value="exchange" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input Form */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold mb-2">
                  {countryCode === 'US' ? '1031 Exchange Analysis' : 'Section 44 Rollover Analysis'}
                </h3>
                <p className="text-sm text-slate-400 mb-4">
                  {countryCode === 'US' 
                    ? 'Defer capital gains taxes by exchanging into a like-kind property within 180 days.'
                    : 'Defer taxes when replacing a business or investment property with a similar property.'
                  }
                </p>
                
                <div className="space-y-4">
                  <h4 className="font-medium text-slate-300">Relinquished Property (Selling)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400 text-sm">Original Purchase Price</Label>
                      <Input
                        type="number"
                        value={exchangeInputs.original_purchase_price}
                        onChange={(e) => setExchangeInputs({...exchangeInputs, original_purchase_price: Number(e.target.value)})}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm">Purchase Year</Label>
                      <Input
                        type="number"
                        value={exchangeInputs.original_purchase_year}
                        onChange={(e) => setExchangeInputs({...exchangeInputs, original_purchase_year: Number(e.target.value)})}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm">Improvements Made</Label>
                      <Input
                        type="number"
                        value={exchangeInputs.improvements_made}
                        onChange={(e) => setExchangeInputs({...exchangeInputs, improvements_made: Number(e.target.value)})}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm">Accumulated Depreciation</Label>
                      <Input
                        type="number"
                        value={exchangeInputs.accumulated_depreciation}
                        onChange={(e) => setExchangeInputs({...exchangeInputs, accumulated_depreciation: Number(e.target.value)})}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm">Sale Price</Label>
                      <Input
                        type="number"
                        value={exchangeInputs.sale_price}
                        onChange={(e) => setExchangeInputs({...exchangeInputs, sale_price: Number(e.target.value)})}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm">Sale Year</Label>
                      <Input
                        type="number"
                        value={exchangeInputs.sale_year}
                        onChange={(e) => setExchangeInputs({...exchangeInputs, sale_year: Number(e.target.value)})}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                  </div>

                  <h4 className="font-medium text-slate-300 pt-4">Replacement Property (Buying)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400 text-sm">Replacement Price</Label>
                      <Input
                        type="number"
                        value={exchangeInputs.replacement_price}
                        onChange={(e) => setExchangeInputs({...exchangeInputs, replacement_price: Number(e.target.value)})}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm">Down Payment %</Label>
                      <Input
                        type="number"
                        value={exchangeInputs.replacement_down_payment_percent}
                        onChange={(e) => setExchangeInputs({...exchangeInputs, replacement_down_payment_percent: Number(e.target.value)})}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  className="w-full mt-6 bg-violet-500 hover:bg-violet-600 text-white"
                  onClick={runExchangeAnalysis}
                  disabled={loading}
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ArrowLeftRight className="w-4 h-4 mr-2" />
                  )}
                  Calculate {countryCode === 'US' ? '1031 Exchange' : 'Section 44 Rollover'}
                </Button>
              </div>

              {/* Results */}
              <div className="space-y-4">
                {exchangeResults?.eligible ? (
                  <>
                    {/* Gain Analysis */}
                    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                      <h3 className="text-lg font-semibold mb-4">Gain Analysis</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Total Gain</span>
                          <span className="font-medium">{formatCurrency(exchangeResults.gain_analysis.total_gain)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Depreciation Recapture</span>
                          <span className="font-medium text-amber-400">{formatCurrency(exchangeResults.gain_analysis.depreciation_recapture)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Capital Gain</span>
                          <span className="font-medium">{formatCurrency(exchangeResults.gain_analysis.capital_gain)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Tax Comparison */}
                    <div className="bg-gradient-to-br from-red-500/20 to-emerald-500/20 rounded-xl p-6 border border-slate-700">
                      <h3 className="text-lg font-semibold mb-4">Tax If Sold Outright</h3>
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Depreciation Recapture Tax</span>
                          <span>{formatCurrency(exchangeResults.tax_if_sold.depreciation_recapture_tax)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Capital Gains Tax</span>
                          <span>{formatCurrency(exchangeResults.tax_if_sold.capital_gains_tax)}</span>
                        </div>
                        {exchangeResults.tax_if_sold.state_tax > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">State Tax</span>
                            <span>{formatCurrency(exchangeResults.tax_if_sold.state_tax)}</span>
                          </div>
                        )}
                        {exchangeResults.tax_if_sold.niit > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">NIIT (3.8%)</span>
                            <span>{formatCurrency(exchangeResults.tax_if_sold.niit)}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-between border-t border-slate-600 pt-3">
                        <span className="font-medium">Total Tax</span>
                        <span className="font-bold text-red-400">{formatCurrency(exchangeResults.tax_if_sold.total_tax)}</span>
                      </div>
                    </div>

                    {/* Exchange Benefit */}
                    <div className="bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-xl p-6 border border-emerald-500/30">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                        {exchangeResults.exchange_type} Benefit
                      </h3>
                      <div className="text-center mb-4">
                        <div className="text-3xl font-bold text-emerald-400">
                          {formatCurrency(exchangeResults.exchange_benefit.tax_deferred)}
                        </div>
                        <div className="text-sm text-slate-400">Tax Deferred</div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Cash Available if Sold</span>
                          <span>{formatCurrency(exchangeResults.exchange_benefit.cash_available_if_sold)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Cash Available via Exchange</span>
                          <span className="text-emerald-400">{formatCurrency(exchangeResults.exchange_benefit.cash_available_if_exchanged)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Replacement Property */}
                    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                      <h3 className="text-lg font-semibold mb-4">Replacement Property</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Purchase Price</span>
                          <span className="font-medium">{formatCurrency(exchangeResults.replacement_property.purchase_price)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Down Payment</span>
                          <span className="font-medium">{formatCurrency(exchangeResults.replacement_property.down_payment)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">New Basis (Exchange)</span>
                          <span className="font-medium text-cyan-400">{formatCurrency(exchangeResults.replacement_property.new_basis_exchange)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Exchange Rules */}
                    {exchangeResults.exchange_rules && (
                      <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <Info className="w-4 h-4 text-amber-500" />
                          Key Rules
                        </h4>
                        <ul className="text-sm text-slate-400 space-y-1">
                          {exchangeResults.exchange_rules.identification_period_days && (
                            <li>• Identify replacement within {exchangeResults.exchange_rules.identification_period_days} days</li>
                          )}
                          {exchangeResults.exchange_rules.exchange_period_days && (
                            <li>• Complete exchange within {exchangeResults.exchange_rules.exchange_period_days} days</li>
                          )}
                          {exchangeResults.exchange_rules.replacement_period_years && (
                            <li>• Replacement period: {exchangeResults.exchange_rules.replacement_period_years} year(s)</li>
                          )}
                          <li>• {exchangeResults.exchange_rules.like_kind_requirement}</li>
                        </ul>
                      </div>
                    )}

                    {/* Modelled tax scenario */}
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 text-center">
                      <p className="text-sm text-slate-300">{exchangeResults.recommendation}</p>
                      <p className="mt-2 text-xs text-slate-500">
                        Tax outputs are estimates for informational purposes only,
                        not tax, legal, financial, or investment advice. Consult
                        qualified professionals for your circumstances.
                      </p>
                    </div>
                  </>
                ) : exchangeResults?.eligible === false ? (
                  <div className="bg-red-500/10 rounded-xl p-6 border border-red-500/30">
                    <AlertCircle className="w-8 h-8 text-red-500 mb-3" />
                    <h3 className="font-semibold mb-2">Not Eligible</h3>
                    <p className="text-sm text-slate-400">{exchangeResults.message}</p>
                  </div>
                ) : (
                  <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center text-slate-400">
                    <ArrowLeftRight className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Enter property details and click Calculate to see tax deferral benefits</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
